import React, { useCallback, memo, useState } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import styled from "styled-components";
import logger from "../../utils/LoggerFrontend";
import {
  Progress,
  MainButton,
  Flexbox,
  BackIcon,
  AuthenticationPageLayout
} from "@talentbait/shared-components";

import Actions from "../../Actions";
import { validateBasics, validateLocation, hasErrors } from "./model";
import { prepareAtsDoc } from "../SelfServiceCampaignCreation/QualificationQuestions/utils";

import CampaignPreview from "./widgets/CampaignPreview";
import CampaignMap from "./widgets/CampaignMap";
import CampaignGraphicLayout from "./widgets/CampaignGraphicLayout";

import BasicsStep from "./pages/Basics";
import LocationStep from "./pages/Location";
import ChannelsInfo from "./pages/ChannelsInfo";
import AiInfo from "./pages/AiInfo";
import AudienceInfo from "./pages/AudienceInfo";
import FinalInfo from "./pages/FinalInfo";
import classNames from "classnames";

const emptyCampaign = {
  jobTitle: "",
  companyName: "",
  campaignName: "",
  jobPostingUrl: "",
  // TODO: mainText and image are not a valid campaign field
  mainText: "",
  image: {},
  activeDate: "",
  campaignDuration: 30,
  startDate: null,
  locations: [],
  pictures: [],
  packages: [],
  selectedChannels: [],
  channels: [],
  lifetimeBudget: "",
  source: "WEB_INTERFACE",
  status: "DRAFT",
  bookingStyle: "",
  lastEditStage: ""
};

const GRAPHICS_PATH = "/images/graphics";

const steps = [
  {
    name: "channels-info",
    Step: ChannelsInfo,
    StepGraphic: () => <img src={`${GRAPHICS_PATH}/channels.svg`} alt="Channels info step" />,
    large: true
  },
  {
    name: "ai-info",
    Step: AiInfo,
    StepGraphic: () => <img src={`${GRAPHICS_PATH}/ai.svg`} alt="AI info step" />
  },
  {
    name: "audience-info",
    Step: AudienceInfo,
    StepGraphic: () => <img src={`${GRAPHICS_PATH}/audience.svg`} alt="Audience info step" />
  },
  {
    name: "final-info",
    Step: FinalInfo,
    StepGraphic: () => <img src={`${GRAPHICS_PATH}/thanks.svg`} alt="Thanks info step" />
  },
  {
    name: "basics",
    Step: BasicsStep,
    StepWidget: CampaignPreview,
    validator: validateBasics
  },
  {
    name: "audience",
    Step: LocationStep,
    StepWidget: CampaignMap,
    validator: validateLocation
  }
];

const StyledAuthenticationPageLayout = styled(AuthenticationPageLayout)`
  align-items: stretch;

  .control-bar {
    min-height: 32px;
  }

  .campaign-progress {
    margin-left: auto;
  }

  .form-container {
    height: 100%;
  }

  .form-content {
    min-height: 0;
    flex: 1;
  }

  .back-button {
    align-self: flex-start;
  }

  @media (min-height: 800px) and (min-width: 420px) {
    .control-bar {
      margin-top: 40px;
      flex-direction: column;
      gap: 30px;
      min-height: 80px;
      justify-content: flex-end;
    }

    .campaign-progress {
      margin-left: 0px;
    }
  }

  @media (min-height: 900px) and (min-width: 420px) {
    .control-bar {
      margin-top: 80px;
    }
  }
`;

const DraftCampaign = ({
  history,
  match,
  organization,
  userProfile,
  user,
  formality,
  questions,
  selectedChatbotQuestion,
  googleApiKey = /*process.env.GOOGLE_API_KEY ||*/ "AIzaSyC_Jamr7BU0zLsUJFeliWVEEpFT5bNcU-c"
}) => {
  const { t } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const [serverError, setServerError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const currentStep = Math.max(
    0,
    steps.findIndex((step) => step.name === match.params.step)
  );

  const { StepWidget, StepGraphic, large } = steps[currentStep];

  const [state, setState] = useState(emptyCampaign);

  const onBack = useCallback(() => {
    const prevStep = steps[currentStep - 1];
    if (prevStep) {
      setValidationErrors({});
      history.push(`/onboarding/${prevStep.name}`);
    }
  }, [currentStep, history]);

  const onSubmit = useCallback(async () => {
    try {
      const { validator } = steps[currentStep];

      if (validator) {
        const newValidationErrors = validator(state, t);
        if (hasErrors(newValidationErrors)) {
          setValidationErrors(newValidationErrors);
          return;
        }
      }

      if (currentStep === steps.length - 1) {
        setLoading(true);
        const orgaId = userProfile.organizationId;
        // Create a draft campaign
        await Actions.createCampaignDraft({
          draftData: {
            ...state,
            jobTitle: state.campaignName,
            locations: state.locations.map((location) => ({
              ..._.omit(location, ["bounds", "value"]),
              radius: 80
            })),
            organizationId: userProfile?.organizationId,
            orgaName: organization?.name,
            companyName: organization?.name,
            userId: userProfile?.userId,
            campaignGoal: organization?.campaignGoal,
            userName: userProfile?.userName,
            bookingStyle: organization?.bookingStyle,
            createdDate: new Date().toISOString(),
            sourceTrigger: "WEB_INTERFACE_1",
            step: "form",
            type: "assistant",
            isOnboardingCampaign: true
          },
          orgaId,
          previewPosts: [],
          campaignGoal: organization?.campaignGoal,
          previewAtsData: prepareAtsDoc({
            formality,
            questions,
            selectedChatbotQuestion,
            orgaId,
            applicationEmail: organization?.applicationEmail
          })
        });
        // Send events to analytics
        if (window.analytics) {
          window.analytics.track("Campaign Draft Created", {
            source: "onboarding",
            organizationId: orgaId,
            organizationName: organization?.name,
            userId: userProfile?.userId,
            userName: userProfile?.userName,
            jobTitle: state.campaignName
          });
          window.analytics.track("Onboarding finished", {
            organizationId: orgaId,
            organizationName: organization?.name,
            userId: userProfile?.userId,
            userName: userProfile?.userName,
            jobTitle: state.campaignName
          });
        }
        history.push("/assistant-dashboard");
        window.scrollTo({ top: 0, left: 0 });
        Actions.loadUserProfile();
      } else {
        const nextStep = steps[currentStep + 1];
        history.push(`/onboarding/${nextStep.name}`);
        window.scrollTo({ top: 0, left: 0 });
      }
      setValidationErrors({});
    } catch (err) {
      setServerError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentStep, state, history, userProfile, organization, t]);

  const onSkip = useCallback(() => history.push("/dashboard"), [history]);

  const onChange = useCallback((value, id) => {
    setState((draft) => ({ ...draft, [id]: value }));
    setValidationErrors((errors) => ({ ...errors, [id]: null }));
  }, []);

  // Push to login if user is not logged in
  if (!user) {
    logger.log("onboarding:redirect", "user not logged in", userProfile);
    const params = new URLSearchParams({ url: "/onboarding" });
    return <Redirect to={`/login?${params.toString()}`} />;
  }

  return (
    <StyledAuthenticationPageLayout
      isMobileGraphicVisible
      isGraphicSticky
      compact={!!StepWidget}
      leftGraphic={
        StepWidget ? (
          <StepWidget campaign={state} organization={organization} />
        ) : (
          <CampaignGraphicLayout as={StepGraphic} className={classNames({ large })} />
        )
      }
    >
      <Flexbox className="form-container" gap={32}>
        <Flexbox row alignItem="center" justifyContent="space-between" className="control-bar">
          {currentStep !== 0 && (
            <MainButton
              variant="secondary"
              size="small"
              onClick={onBack}
              text={t("Back")}
              className="back-button"
              Icon={BackIcon}
            />
          )}
          <Progress current={currentStep} total={steps.length} className="campaign-progress" />
        </Flexbox>

        <Switch>
          {steps.map(({ Step, name }, idx) => (
            <Route
              key={idx}
              exact="true"
              path={`/onboarding/${name}`}
              render={(props) => (
                <Step
                  {...props}
                  state={state}
                  errors={validationErrors}
                  onChange={onChange}
                  onSubmit={onSubmit}
                  onSkip={onSkip}
                  loading={loading}
                  className="form-content"
                  first={idx === 0}
                  last={idx === steps.length - 1}
                  googleApiKey={googleApiKey}
                  namespace={`draft-campaigns:${name}`}
                />
              )}
            />
          ))}

          <Redirect to={`/onboarding/${steps[0].name}`} />
        </Switch>
      </Flexbox>
    </StyledAuthenticationPageLayout>
  );
};

export default memo(DraftCampaign);
